import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  useWindowDimensions,
  View,
  type ViewStyle,
} from "react-native";

import { createCompetition, listMyCompetitions } from "../../../../lib/clientCompetitions";
import { getMyCompanies } from "../../../../lib/companies";
import { writeActiveCaseId } from "../../../../lib/useActiveCase";
import type { ClientCompetitionListRow, Company } from "../../../../types/database";
import { COLORS, FONTS } from "../../../../lib/brand";
import { PANE_THEMES } from "../../../../components/brand/paneThemes";

const THEME = PANE_THEMES.Client;
const AMBER = THEME.accent;
const GOLD = COLORS.GOLD_RM.hex;
const FOREST = THEME.sidebarBg;
const OFF_BLACK = THEME.surfaceBg;
const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const RED = THEME.destructive;

function firstParam(v: string | string[] | undefined): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

export default function NewCompetitionScreen() {
  const params = useLocalSearchParams<{ engagement?: string | string[] }>();
  const engagement = firstParam(params.engagement);
  // Step 2 (licensing form) is gated on this param. Step 1 (engagement
  // picker) renders otherwise — including when `engagement` is anything
  // other than "licensing".
  const showLicensingForm = engagement === "licensing";

  if (showLicensingForm) return <LicensingStep />;
  return <EngagementPickerStep />;
}

// ─── Step 1 ───────────────────────────────────────────────────────────────

function EngagementPickerStep() {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};
  const transitionWeb =
    Platform.OS === "web"
      ? ({
          transition:
            "background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
        } as unknown as ViewStyle)
      : null;
  const { width: viewportWidth } = useWindowDimensions();
  const sideBySide = viewportWidth >= 720;

  const [hoverRm, setHoverRm] = useState(false);
  const [hoverLic, setHoverLic] = useState(false);

  return (
    <ScrollView
      style={{ flex: 1 }}
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{ paddingBottom: 80 }}
    >
      <View style={{ paddingHorizontal: 32, paddingTop: 28, maxWidth: 980 }}>
        <Pressable
          onPress={() => {
            if (router.canGoBack()) router.back();
            else router.replace("/client" as Href);
          }}
          style={[cursorWeb, { alignSelf: "flex-start", paddingVertical: 4 }]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyMedium,
              fontSize: 11.5,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: WM,
            }}
          >
            {"← Back"}
          </Text>
        </Pressable>

        <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginTop: 14 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Step 1 · New program
          </Text>
        </View>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.display,
            fontSize: 36,
            lineHeight: 40,
            color: W,
          }}
        >
          What kind of <Text style={{ fontStyle: "italic", color: AMBER }}>program</Text>?
        </Text>
        <Text
          style={{
            marginTop: 10,
            fontFamily: FONTS.bodyLight,
            fontSize: 14,
            lineHeight: 22,
            color: WM,
            maxWidth: 720,
          }}
        >
          Pick the engagement that fits. You can always switch later — just talk to your account manager.
        </Text>
      </View>

      <View style={{ paddingHorizontal: 32, marginTop: 24, maxWidth: 980 }}>
        <View
          style={{
            flexDirection: sideBySide ? "row" : "column",
            gap: 16,
          }}
        >
          {/* Coravu RM (gold treatment) */}
          <Pressable
            onPress={() => router.push("/client/competition/new/book" as Href)}
            onHoverIn={() => setHoverRm(true)}
            onHoverOut={() => setHoverRm(false)}
            style={[
              {
                flex: 1,
                minWidth: 280,
                padding: 24,
                borderRadius: 6,
                borderWidth: 1.5,
                borderColor: hoverRm ? GOLD : "rgba(201,162,74,0.55)",
                backgroundColor: hoverRm
                  ? "rgba(201,162,74,0.10)"
                  : "rgba(201,162,74,0.04)",
              } as ViewStyle,
              transitionWeb,
              cursorWeb,
            ]}
          >
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <View style={{ width: 18, height: 2, backgroundColor: GOLD }} />
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 9,
                  letterSpacing: 2.4,
                  textTransform: "uppercase",
                  color: GOLD,
                }}
              >
                Coravu Managed
              </Text>
            </View>
            <Text
              style={{
                marginTop: 14,
                fontFamily: FONTS.displayMedium,
                fontSize: 26,
                lineHeight: 30,
                color: W,
              }}
            >
              Coravu runs <Text style={{ fontStyle: "italic", color: GOLD }}>it for me</Text>.
            </Text>
            <Text
              style={{
                marginTop: 10,
                fontFamily: FONTS.body,
                fontSize: 13,
                lineHeight: 20,
                color: WS,
              }}
            >
              We design the case, run the program, judge the work, and surface the talent. Zero internal lift. Starts with a discovery call to scope your program.
            </Text>
            <View style={{ marginTop: 18, flexDirection: "row", alignItems: "center", gap: 10 }}>
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 12,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: GOLD,
                }}
              >
                {"Book a discovery call →"}
              </Text>
            </View>
          </Pressable>

          {/* Licensing */}
          <Pressable
            onPress={() => router.push("/client/competition/new?engagement=licensing" as Href)}
            onHoverIn={() => setHoverLic(true)}
            onHoverOut={() => setHoverLic(false)}
            style={[
              {
                flex: 1,
                minWidth: 280,
                padding: 24,
                borderRadius: 6,
                borderWidth: 1,
                borderColor: hoverLic ? "rgba(255,255,255,0.32)" : BORDER,
                backgroundColor: hoverLic ? "rgba(255,255,255,0.02)" : "transparent",
              } as ViewStyle,
              transitionWeb,
              cursorWeb,
            ]}
          >
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <View style={{ width: 18, height: 2, backgroundColor: "rgba(255,255,255,0.4)" }} />
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 9,
                  letterSpacing: 2.4,
                  textTransform: "uppercase",
                  color: WM,
                }}
              >
                Self-Managed
              </Text>
            </View>
            <Text
              style={{
                marginTop: 14,
                fontFamily: FONTS.displayMedium,
                fontSize: 26,
                lineHeight: 30,
                color: W,
              }}
            >
              {"I'll run my own program."}
            </Text>
            <Text
              style={{
                marginTop: 10,
                fontFamily: FONTS.body,
                fontSize: 13,
                lineHeight: 20,
                color: WS,
              }}
            >
              You design the brief, set deliverables, judge the work, and pick the talent. Coravu provides the platform, the student pool, and the case-management tools.
            </Text>
            <View style={{ marginTop: 18, flexDirection: "row", alignItems: "center", gap: 10 }}>
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 12,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: AMBER,
                }}
              >
                {"Continue with Self-Managed →"}
              </Text>
            </View>
          </Pressable>
        </View>

        <Text
          style={{
            marginTop: 18,
            fontFamily: FONTS.body,
            fontSize: 11.5,
            lineHeight: 16,
            color: WL,
          }}
        >
          Not sure which fits? Pick Coravu Managed and book a call — Tyler will walk you through both options.
        </Text>
      </View>
    </ScrollView>
  );
}

// ─── Step 2 (Licensing form) ─────────────────────────────────────────────

function LicensingStep() {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [brief, setBrief] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Unused but kept loaded — same shape as the prior version.
  const [, setCases] = useState<ClientCompetitionListRow[]>([]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      const [list, comps] = await Promise.all([getMyCompanies(), listMyCompetitions()]);
      setCompanies(list);
      if (list.length === 1) setCompanyId(list[0].id);
      setCases(comps);
      setLoading(false);
    })();
  }, []);

  useFocusEffect(
    useCallback(() => {
      void (async () => {
        const comps = await listMyCompetitions();
        setCases(comps);
      })();
    }, [])
  );

  const cleanName = name.trim();
  const canSubmit = !saving && companies.length > 0 && cleanName.length > 0;

  async function onSave() {
    if (saving) return;
    const cid = companyId ?? companies[0]?.id;
    if (!cid) {
      setErr("No company is linked to your account yet. Reach out to support if this looks wrong.");
      return;
    }
    if (!cleanName) {
      setErr("Give your case a name to continue.");
      return;
    }
    setSaving(true);
    setErr(null);
    const { id, error } = await createCompetition({
      company_id: cid,
      engagement_model: "licensing",
      name: cleanName,
      brief: brief.trim() || null,
      deliverable_description: null,
    });
    setSaving(false);
    if (error || !id) {
      setErr(error?.message ?? "Could not create the case. Please try again.");
      return;
    }
    writeActiveCaseId(id);
    router.replace(`/client/competition/${id}/edit` as Href);
  }

  if (loading) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center", paddingVertical: 80 }}>
        <ActivityIndicator size="small" color={AMBER} />
      </View>
    );
  }

  return (
    <ScrollView
      style={{ flex: 1 }}
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{ paddingBottom: 80 }}
    >
      <View style={{ paddingHorizontal: 32, paddingTop: 28, maxWidth: 760 }}>
        <Pressable
          onPress={() => router.replace("/client/competition/new" as Href)}
          style={[cursorWeb, { alignSelf: "flex-start", paddingVertical: 4 }]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyMedium,
              fontSize: 11.5,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: WM,
            }}
          >
            {"← Back to engagement picker"}
          </Text>
        </Pressable>

        <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginTop: 14 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Step 2 · Self-Managed program
          </Text>
        </View>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.display,
            fontSize: 36,
            lineHeight: 40,
            color: W,
          }}
        >
          Name your <Text style={{ fontStyle: "italic", color: AMBER }}>case</Text>.
        </Text>
        <Text
          style={{
            marginTop: 10,
            fontFamily: FONTS.bodyLight,
            fontSize: 14,
            lineHeight: 22,
            color: WM,
            maxWidth: 620,
          }}
        >
          {"Just give it a name to start — you can add the brief, deliverables, dates, and eligibility filters in the editor next. Drafts are private to your team until you publish."}
        </Text>
      </View>

      {companies.length === 0 ? (
        <View style={{ paddingHorizontal: 32, marginTop: 24, maxWidth: 760 }}>
          <View
            style={{
              backgroundColor: OFF_BLACK,
              borderWidth: 1,
              borderColor: "rgba(230,154,138,0.35)",
              borderRadius: 4,
              padding: 22,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: RED,
              }}
            >
              No company on file
            </Text>
            <Text
              style={{
                marginTop: 8,
                fontFamily: FONTS.displayMedium,
                fontSize: 22,
                color: W,
              }}
            >
              We need a company linked to your account before you can post a case.
            </Text>
            <Text
              style={{
                marginTop: 6,
                fontFamily: FONTS.body,
                fontSize: 13.5,
                lineHeight: 20,
                color: WM,
              }}
            >
              {"If you signed up recently this should already be set up. Reach out to luke@coravu.com and we'll fix it."}
            </Text>
          </View>
        </View>
      ) : (
        <View style={{ paddingHorizontal: 32, marginTop: 24, maxWidth: 760 }}>
          <View
            style={{
              backgroundColor: OFF_BLACK,
              borderWidth: 1,
              borderColor: BORDER,
              borderRadius: 4,
              padding: 26,
            }}
          >
            {companies.length > 1 ? (
              <View style={{ marginBottom: 22 }}>
                <FieldLabel>Company</FieldLabel>
                <View style={{ marginTop: 10, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                  {companies.map((c) => {
                    const selected = companyId === c.id;
                    return (
                      <Pressable
                        key={c.id}
                        onPress={() => setCompanyId(c.id)}
                        style={[
                          {
                            paddingHorizontal: 14,
                            paddingVertical: 8,
                            borderRadius: 999,
                            borderWidth: 1,
                            borderColor: selected ? AMBER : BORDER,
                            backgroundColor: selected ? "rgba(181,135,58,0.12)" : "transparent",
                          } as ViewStyle,
                          cursorWeb,
                        ]}
                      >
                        <Text
                          style={{
                            fontFamily: FONTS.bodyMedium,
                            fontSize: 12,
                            color: selected ? AMBER : W,
                          }}
                        >
                          {c.name}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            ) : null}

            <View>
              <View style={{ flexDirection: "row", alignItems: "baseline", gap: 6 }}>
                <FieldLabel>Name</FieldLabel>
                <Text style={{ color: RED, fontFamily: FONTS.bodyBold, fontSize: 9 }}>*</Text>
              </View>
              <TextInput
                value={name}
                onChangeText={setName}
                placeholder="e.g. Q3 Product Strategy Cohort"
                placeholderTextColor={WL}
                autoFocus
                style={{
                  marginTop: 8,
                  backgroundColor: "rgba(0,0,0,0.4)",
                  borderWidth: 1,
                  borderColor: BORDER,
                  borderRadius: 3,
                  paddingHorizontal: 14,
                  paddingVertical: 12,
                  color: W,
                  fontFamily: FONTS.displayMedium,
                  fontSize: 18,
                }}
              />
              <Text
                style={{
                  marginTop: 6,
                  fontFamily: FONTS.body,
                  fontSize: 11.5,
                  color: WL,
                }}
              >
                Students see this on their dashboard. You can rename it later.
              </Text>
            </View>

            <View style={{ marginTop: 22 }}>
              <FieldLabel>Brief</FieldLabel>
              <TextInput
                value={brief}
                onChangeText={setBrief}
                placeholder={"A one-paragraph description of the problem you're putting in front of students. You can write this later — it's optional now."}
                placeholderTextColor={WL}
                multiline
                textAlignVertical="top"
                style={{
                  marginTop: 8,
                  backgroundColor: "rgba(0,0,0,0.4)",
                  borderWidth: 1,
                  borderColor: BORDER,
                  borderRadius: 3,
                  paddingHorizontal: 14,
                  paddingVertical: 12,
                  minHeight: 120,
                  color: W,
                  fontFamily: FONTS.body,
                  fontSize: 13.5,
                  lineHeight: 20,
                }}
              />
              <Text
                style={{
                  marginTop: 6,
                  fontFamily: FONTS.body,
                  fontSize: 11.5,
                  color: WL,
                }}
              >
                Optional. You can polish this in the editor.
              </Text>
            </View>
          </View>

          <View
            style={{
              marginTop: 18,
              paddingHorizontal: 22,
              paddingVertical: 18,
              borderRadius: 4,
              backgroundColor: "rgba(181,135,58,0.05)",
              borderWidth: 1,
              borderColor: "rgba(181,135,58,0.18)",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              What happens next
            </Text>
            <Text
              style={{
                marginTop: 8,
                fontFamily: FONTS.body,
                fontSize: 12.5,
                lineHeight: 19,
                color: WM,
              }}
            >
              {"We'll save a draft and drop you into the editor. From there you add deliverables and a rubric, set the registration window and the close date, and tune eligibility (schools, majors, graduation years). Nothing is visible to students until you publish."}
            </Text>
          </View>

          {err ? (
            <View
              style={{
                marginTop: 16,
                paddingHorizontal: 14,
                paddingVertical: 12,
                borderRadius: 3,
                backgroundColor: "rgba(230,154,138,0.08)",
                borderWidth: 1,
                borderColor: "rgba(230,154,138,0.4)",
              }}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: RED }}>{err}</Text>
            </View>
          ) : null}

          <View
            style={{
              marginTop: 24,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 18,
            }}
          >
            <Pressable
              onPress={() => router.replace("/client/competition/new" as Href)}
              style={[cursorWeb, { paddingVertical: 8 }]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyMedium,
                  fontSize: 12,
                  letterSpacing: 0.6,
                  color: WM,
                }}
              >
                Cancel
              </Text>
            </Pressable>

            <Pressable
              onPress={() => void onSave()}
              disabled={!canSubmit}
              style={[
                {
                  paddingHorizontal: 22,
                  paddingVertical: 13,
                  backgroundColor: canSubmit ? AMBER : "rgba(181,135,58,0.35)",
                  borderRadius: 3,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                } as ViewStyle,
                canSubmit ? cursorWeb : null,
              ]}
            >
              {saving ? <ActivityIndicator size="small" color={FOREST} /> : null}
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 12,
                  letterSpacing: 1.8,
                  textTransform: "uppercase",
                  color: FOREST,
                }}
              >
                {saving ? "Creating…" : "Create draft →"}
              </Text>
            </Pressable>
          </View>
        </View>
      )}
    </ScrollView>
  );
}

function FieldLabel({ children }: { children: string }) {
  return (
    <Text
      style={{
        fontFamily: FONTS.bodyBold,
        fontSize: 9,
        letterSpacing: 2,
        textTransform: "uppercase",
        color: "rgba(255,255,255,0.55)",
      }}
    >
      {children}
    </Text>
  );
}
